import { use, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CalendarSongListType,
  EventSongList,
  OrgType,
  SongListEntryType,
  SongListType,
} from '../../../../../common/src/model.ts';
import { wasEventSongListEdited } from '../../../../../common/src/model_util.ts';
import { getShortTime, parseInputAsTimeString } from '../../../../../common/src/time_string.ts';
import TrashIcon from '../../../assets/trash.svg?react';
import { useMappedSongListId } from '../../../data/song_list_id_map.tsx';
import { useCurrentOrg } from '../../../data/use_current_org.tsx';
import { useCustomMusic } from '../../../data/use_custom_music.tsx';
import { HymnalsContext } from '../../../data/use_hymnals.tsx';
import { useUserAttributes } from '../../../data/use_user_attributes.ts';
import { confirm } from '../../../util/confirm.tsx';
import { Device, getDevice } from '../../../util/platform.ts';
import { useEffectOnce } from '../../../util/use_effect_once.ts';
import { useHoldAppReload } from '../../../util/use_hold_app_reload.ts';
import { DownloadSongSheetsButton } from './download_song_sheets.tsx';
import { MinSongListRowCount, SongRowHeightSingle, SongRowWidth } from './shared.tsx';
import { SongCell } from './song_cell.tsx';
import { useAllSongLists } from './use_all_song_lists.ts';
import { usePlannerKeyboardNavigation } from './use_planner_keyboard_navigation.tsx';

export interface EditEventProps {
  defaultName?: string;
  onClose: () => void;
  songListId: number;
  songListType: CalendarSongListType;
}

export const EventEditor = ({ defaultName, onClose, songListId: songListIdRaw, songListType }: EditEventProps) => {
  const { customMusic } = useCustomMusic();
  const hymnals = use(HymnalsContext);
  const allSongLists = useAllSongLists();
  const dialogElementRef = useRef<HTMLDialogElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const songListId = useMappedSongListId({ rawId: songListIdRaw });
  const songList = allSongLists.find((list) => list.id === songListIdRaw || list.id === songListId) as
    EventSongList | undefined;
  const [nameChanged, setNameChanged] = useState(false);
  const { isInternalUser, isOrgAdmin, isStaff } = useUserAttributes();
  const { currentOrg } = useCurrentOrg();

  // this editor is mounted only while its dialog is open, and its name, date and time are committed
  // on close, so a deploy must not reload the page under it (issue #3265)
  useHoldAppReload(true);

  const onCloseDialog = useCallback(async () => {
    if (!songList) {
      return;
    }
    const time = parseInputAsTimeString(timeInputRef.current?.value);
    if (!wasEventSongListEdited(songList) && !nameChanged && !time) {
      await allSongLists.remove(songList);
    } else {
      const name = nameInputRef.current?.value;
      const date = dateInputRef.current?.value;
      const { name: _unused1, time: _unused3, ...nakedList } = songList;
      await allSongLists.update({
        ...nakedList,
        ...(name && { name }),
        ...(date && { date }),
        ...(time && { time }),
      });
    }
    onClose();
  }, [allSongLists, nameInputRef, dateInputRef, timeInputRef, nameChanged, onClose, songList]);

  const onClickBackground = useCallback(
    async (event: MouseEvent) => {
      if (event.target === dialogElementRef.current) {
        await onCloseDialog();
      }
    },
    [onCloseDialog],
  );

  useEffect(() => {
    const controller = new AbortController();
    const { current } = dialogElementRef;
    current?.addEventListener('click', onClickBackground, { signal: controller.signal });
    current?.addEventListener('cancel', onCloseDialog, { signal: controller.signal });
    return () => controller.abort();
  }, [onClickBackground, onCloseDialog]);

  const onDeleteEvent = useCallback(async () => {
    if (
      songListType === SongListType.Seasonal ||
      (await confirm({ confirmation: <span>Are you sure you want to delete this event?</span> }))
    ) {
      if (songList) {
        await allSongLists.remove(songList);
      }
      onClose();
    }
  }, [allSongLists, onClose, songList, songListType]);

  const onNameChanged = useCallback(() => {
    setNameChanged(true);
  }, []);

  const onSelectSong = useCallback(() => {
    if (songListType === SongListType.Seasonal) {
      onClose();
    }
  }, [onClose, songListType]);

  const maxSongRow = songList ? Math.max(...songList.songs.map(({ row }) => row)) : 0;
  const rowCount = songListType === SongListType.Seasonal ? 1 : Math.max(MinSongListRowCount, maxSongRow + 1);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const nextColumn = useCallback(() => 0, []);
  const prevColumn = useCallback(() => 0, []);

  const { moveDown, onKeyDown, selectedCell, setSelectedCell } = usePlannerKeyboardNavigation({
    defaultColumn: 0,
    nextColumn,
    prevColumn,
    rowCount,
    ignoreKeystrokes: isDialogOpen,
    columnCount: 1,
  });

  useEffectOnce(() => {
    const { current } = dialogElementRef;
    current?.showModal();

    // iOS does not allow input controls to be proactively selected
    // and does not handle clicking a control w/selected text
    if (getDevice() === Device.iOS) {
      return;
    }

    if (songList?.name) {
      // avoid selecting existing name
      nameInputRef.current?.blur();
      setSelectedCell({ row: 1, column: 0 });
    } else if (defaultName) {
      nameInputRef.current?.select();
    }
  });

  if (!songList) {
    return null;
  }

  return (
    <Dialog ref={dialogElementRef} $songListType={songListType}>
      <DialogContent>
        {/* TODO(hewitt): iOS fails to focus input (https://medium.com/@brunn/autofocus-in-ios-safari-458215514a5f)*/}
        {songListType !== SongListType.Seasonal && (
          <EventName
            key="name"
            placeholder="Event Name"
            defaultValue={songList.name ?? defaultName}
            onChange={onNameChanged}
            ref={nameInputRef}
          />
        )}

        <EventDetails key="details">
          <EventDate key="date" type="date" defaultValue={songList.date} ref={dateInputRef} />
          {songListType !== SongListType.Seasonal && (
            <>
              at
              <EventTime key="time" defaultValue={getShortTime(songList.time)} ref={timeInputRef} />
              {currentOrg && <DownloadSongSheetsButton org={currentOrg} date={songList.date} type={songListType} />}
            </>
          )}
        </EventDetails>
        <DeleteEventButton key="delete" onClick={onDeleteEvent} />
        <CloseButton key="close" onClick={onCloseDialog}>
          Done
        </CloseButton>
        <ScrollWrapper key="list" onKeyDown={onKeyDown} $songListType={songListType}>
          <SongListWrapper $rowCount={rowCount}>
            {[...Array(rowCount).keys()]
              .map((index) => index + 1)
              .map((row) => (
                <SongCell
                  allSongLists={allSongLists}
                  key={row}
                  customMusic={customMusic}
                  date={songList.date}
                  hymnals={hymnals}
                  moveDown={moveDown}
                  row={row}
                  songList={songList}
                  songListEntryType={SongListEntryType.Song}
                  songListType={songListType}
                  isChurch={currentOrg?.type === OrgType.Church}
                  selectedCell={selectedCell}
                  setSelectedCell={setSelectedCell}
                  setIsDialogOpen={setIsDialogOpen}
                  isInternalUser={isInternalUser}
                  isOrgAdmin={isOrgAdmin}
                  isStaff={isStaff}
                  columnStart={0}
                  columnCount={1}
                  onSelectSong={onSelectSong}
                />
              ))}
          </SongListWrapper>
        </ScrollWrapper>
      </DialogContent>
    </Dialog>
  );
};

// prevents clicks on dialog itself
const DialogContent = styled.div`
  height: 80vh;
  padding: 20px;
  background-color: var(--color-background);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DeleteEventButton = styled(TrashIcon)`
  position: absolute;
  top: 0;
  left: 0;
  margin: 10px;
  fill: var(--color-background);
  background-color: var(--color-trash);
  padding: 7px;
  border-radius: 10px;
  width: 30px;
  cursor: pointer;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 8px 10px;
  margin: 8px 7px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.75rem;
  color: var(--color-background);
  background-color: var(--color-primary-button);
  border-radius: 20px;
  cursor: pointer;
`;

const Dialog = styled.dialog<{ $songListType: SongListType }>`
  height: ${(props) => (props.$songListType === SongListType.Seasonal ? '20vh' : '80vh')};
  overflow: hidden;
  padding: 0; // prevent clicks on dialog itself
  margin: auto;
  border-radius: 15px;
  border: none;
  overscroll-behavior-x: none; /* avoid horizontal dialog scroll on iOS */
`;

const EventName = styled.input`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: min(max(1.5vw, 1.1rem), 2rem);
  border: none;
  text-align: center;
  color: var(--color-text);
  background-color: transparent;
  padding: 0 20px 20px 20px;
  width: 65vw;
  text-overflow: ellipsis;
`;

const EventDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 1rem;
  color: var(--color-text);
  background-color: transparent;
  text-align: center;
  padding-bottom: 15px;
`;

const EventDate = styled.input`
  width: 13ch;
  text-align: right;
`;

const EventTime = styled.input`
  color: var(--color-text);
  background-color: transparent;
  text-align: center;
  width: 9ch;
`;

const ScrollWrapper = styled.div<{ $songListType: SongListType }>`
  height: ${(props) => (props.$songListType === SongListType.Seasonal ? '10vh' : '62vh')};
  overflow-y: scroll;
  overflow-x: hidden;
  overscroll-behavior-x: none; /* avoid swipe to go back */
  ${(props) => props.$songListType === SongListType.Seasonal && `margin-top: 30px;`}
`;

const SongListWrapper = styled.div<{ $rowCount: number }>`
  display: grid;
  grid-template-columns: repeat(1, min(${SongRowWidth}, 80vw));
  grid-template-rows: repeat(${(props) => props.$rowCount}, ${SongRowHeightSingle});
  grid-gap: 1px; /* avoids cell outline over-reach */
  padding: 3px; /* allows left & right cell outlines to be visible*/
`;
